'use client';

/**
 * ArtistProfileEditDialog
 *
 * Full WYSIWYG edit for an artist profile — accessible to super-admins, admins,
 * and the artist's linked creator account.
 *
 * Fields: name, bio, genres, profile image, gallery images (MultiArtworkUploader),
 *         primary video, additional videos[].
 *
 * Saves to Firestore via updateArtist().
 */

import { useState, useRef } from 'react';
import Image from 'next/image';
import { Loader2, Upload, PlusCircle, Trash2, Pencil, X, Video, Disc, ChevronDown, ChevronUp, Search, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { MultiArtworkUploader } from '@/components/ui/multi-artwork-uploader';
import { TRANSITION_LABELS, type SlideshowTransition } from '@/components/ui/slideshow-image';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import GenresTagInput from '@/components/ui/genres-tag-input';
import { updateArtist } from '@/services/artists';
import { getAllReleases } from '@/services/admin';
import { storage, db } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// Helper to extract YouTube embed ID
function extractYoutubeId(url: string): string | null {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([^&?\s]{11})/);
  return m ? m[1] : null;
}

interface SlideshowSettings { intervalSec?: number; fadeSec?: number; transition?: string }

interface ArtistFormData {
  name: string;
  bio: string;
  genres: string[];
  imageUrl: string;
  images: string[];
  videoUrl: string;
  videos: string[];
  slideshowSettings: SlideshowSettings;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  artist: {
    id: string;
    name: string;
    bio?: string;
    genres?: string[];
    imageUrl?: string;
    images?: string[];
    videoUrl?: string;
    videos?: string[];
    slideshowSettings?: SlideshowSettings;
  };
  onSaved?: (updated: Partial<ArtistFormData>) => void;
}

export function ArtistProfileEditDialog({ open, onOpenChange, artist, onSaved }: Props) {
  const [form, setForm] = useState<ArtistFormData>({
    name: artist.name || '',
    bio: artist.bio || '',
    genres: artist.genres || [],
    imageUrl: artist.imageUrl || '',
    images: artist.images || [],
    videoUrl: artist.videoUrl || '',
    videos: artist.videos || [],
    slideshowSettings: artist.slideshowSettings || {},
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Link Releases panel
  const [releases, setReleases] = useState<any[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releasesExpanded, setReleasesExpanded] = useState(false);
  const [releaseSearch, setReleaseSearch] = useState('');

  const loadReleases = async () => {
    setReleasesLoading(true);
    try { setReleases(await getAllReleases()); } catch { }
    setReleasesLoading(false);
  };

  const isReleaseLinked = (r: any) =>
    r.artistId === artist.id || r.artist?.docId === artist.id ||
    r.artist?.name?.trim().toLowerCase() === artist.name.trim().toLowerCase() ||
    r.artistName?.trim().toLowerCase() === artist.name.trim().toLowerCase();

  const handleLinkRelease = async (release: any, linked: boolean) => {
    try {
      await updateDoc(doc(db, 'releases', release.id), {
        artistId: linked ? artist.id : '',
        artistName: linked ? artist.name : '',
        updatedAt: new Date().toISOString(),
      });
      setReleases(prev => prev.map(r => r.id === release.id
        ? { ...r, artistId: linked ? artist.id : '', artistName: linked ? artist.name : '' }
        : r));
      toast({ title: linked ? `"${release.title}" linked` : `"${release.title}" unlinked` });
    } catch {
      toast({ title: 'Failed to update release link', variant: 'destructive' });
    }
  };

  const handleProfileImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `artists/${artist.id}/profile-${Date.now()}.${ext}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      setForm(f => ({ ...f, imageUrl: url }));
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploadingImage(false);
    }
  };

  const addVideo = () => setForm(f => ({ ...f, videos: [...f.videos, ''] }));
  const updateVideo = (idx: number, val: string) =>
    setForm(f => { const vs = [...f.videos]; vs[idx] = val; return { ...f, videos: vs }; });
  const removeVideo = (idx: number) =>
    setForm(f => ({ ...f, videos: f.videos.filter((_, i) => i !== idx) }));

  /**
   * Upload-on-save for the gallery. MultiArtworkUploader holds picked files as
   * base64 data URLs (FileReader.readAsDataURL). Persisting those straight to
   * Firestore would blow the 1MB doc limit after even one decent-quality JPEG,
   * so we sweep through `form.images`, upload any data:* URLs to Storage in
   * parallel, and replace them with their public download URLs before write.
   * URLs already pointing at http(s) (re-saves, existing gallery images) pass
   * through untouched.
   */
  const uploadDataUrlsToStorage = async (urls: string[]): Promise<string[]> => {
    return Promise.all(urls.map(async (u, i) => {
      if (!u.startsWith('data:')) return u;
      const res = await fetch(u);
      const blob = await res.blob();
      const mime = blob.type || 'image/jpeg';
      const ext = mime.split('/')[1]?.split('+')[0] || 'jpg';
      const path = `artists/${artist.id}/gallery-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
      const snap = await uploadBytes(storageRef(storage, path), blob, { contentType: mime });
      return getDownloadURL(snap.ref);
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Materialize any pending base64 gallery images into Storage URLs first.
      const uploadedImages = await uploadDataUrlsToStorage(form.images);
      const payload: Record<string, unknown> = {
        name: form.name,
        bio: form.bio,
        genres: form.genres,
        imageUrl: form.imageUrl,
        images: uploadedImages,
        videoUrl: form.videoUrl,
        videos: form.videos.filter(Boolean),
        slideshowSettings: form.slideshowSettings,
      };
      await updateArtist(artist.id, payload as any);
      // Keep the Storage URLs in local form state so a quick re-save doesn't
      // re-upload the same files.
      setForm(f => ({ ...f, images: uploadedImages }));
      toast({ title: 'Saved', description: 'Artist profile updated.' });
      onSaved?.({ ...form, images: uploadedImages });
      onOpenChange(false);
    } catch (err) {
      console.error('[artist-edit save]', err);
      toast({ title: 'Save failed', description: 'Image upload or Firestore write failed.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto gap-4 p-5">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-violet-400" />
            Edit Artist Profile
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <Label>Artist Name</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" />
          </div>

          {/* Bio */}
          <div>
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={4} className="mt-1" />
          </div>

          {/* Genres */}
          <div>
            <Label>Genres</Label>
            <div className="mt-1">
              <GenresTagInput value={form.genres} onChange={genres => setForm(f => ({ ...f, genres }))} />
            </div>
          </div>

          {/* Link Releases */}
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
              onClick={() => { setReleasesExpanded(v => !v); if (!releasesExpanded && releases.length === 0) loadReleases(); }}
            >
              <span className="text-sm font-semibold flex items-center gap-1.5">
                <Disc className="h-4 w-4 text-violet-400" />Link Releases
              </span>
              {releasesExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </button>
            {releasesExpanded && (
              <div className="border-t border-border p-3 space-y-3">
                {(() => {
                  const linked = releases.filter(isReleaseLinked);
                  return linked.length > 0 ? (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Linked ({linked.length})</p>
                      <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                        {linked.map((r: any) => (
                          <div key={r.id} className="flex items-center gap-2 p-1.5 rounded bg-primary/5 border border-primary/20">
                            {(r.coverArtUrl || r.imageUrl) && (
                              <Image src={r.coverArtUrl || r.imageUrl} alt={r.title} width={28} height={28} className="rounded object-cover flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{r.title || 'Untitled'}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{r.releaseType || r.type || ''}</p>
                            </div>
                            <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive flex-shrink-0" onClick={() => handleLinkRelease(r, false)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : releasesLoading ? null : <p className="text-xs text-muted-foreground text-center py-1">None linked yet.</p>;
                })()}
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
                      <Input type="search" className="h-7 text-xs pl-7" placeholder="Search releases to link…" value={releaseSearch} onChange={e => setReleaseSearch(e.target.value)} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={loadReleases} disabled={releasesLoading}>
                      {releasesLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </Button>
                  </div>
                  {releaseSearch.trim().length > 0 && (() => {
                    const q = releaseSearch.toLowerCase();
                    const unlinked = releases.filter(r => !isReleaseLinked(r) && (r.title || '').toLowerCase().includes(q)).slice(0, 8);
                    return unlinked.length > 0 ? (
                      <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                        {unlinked.map((r: any) => (
                          <div key={r.id} className="flex items-center gap-2 p-1.5 rounded border border-border/50 hover:bg-muted/50">
                            {(r.coverArtUrl || r.imageUrl) && (
                              <Image src={r.coverArtUrl || r.imageUrl} alt={r.title} width={28} height={28} className="rounded object-cover flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{r.title || 'Untitled'}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{r.releaseType || r.type || ''}</p>
                            </div>
                            <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2 flex-shrink-0" onClick={() => handleLinkRelease(r, true)}>Link</Button>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground text-center py-1">No matches.</p>;
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Profile image */}
          <div className="space-y-1.5">
            <Label>Profile Image</Label>
            <div className="flex gap-3 items-start">
              {form.imageUrl && (
                <div className="relative h-20 w-20 rounded-lg overflow-hidden flex-shrink-0 border border-border/40">
                  <Image src={form.imageUrl} alt="Profile" fill className="object-cover" />
                  <button
                    onClick={() => setForm(f => ({ ...f, imageUrl: '' }))}
                    className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <div className="flex-1 space-y-1.5">
                <Input
                  value={form.imageUrl}
                  onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                  placeholder="Image URL"
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs gap-1.5"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleProfileImageUpload(e.target.files[0]); }}
                />
              </div>
            </div>
          </div>

          {/* Gallery — images, GIFs, and short video clips all live in the
              same array (artist.images[]); MultiArtworkUploader's
              acceptVideo mode detects mp4/webm/mov per-file on add and
              per-URL on render (isVideoUrl). */}
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label>Gallery (images, GIFs &amp; videos)</Label>
              <span className="text-[10px] text-muted-foreground">
                Hold ⌘/Ctrl in the picker to select multiple
              </span>
            </div>
            <MultiArtworkUploader
              images={form.images}
              onChange={imgs => setForm(f => ({ ...f, images: imgs }))}
              maxImages={12}
              acceptVideo
              addLabel="Add Image / GIF / Video"
            />
          </div>

          {/* Card thumbnail autoplay — every artist card (grid/list) cycles
              through the gallery above at a default tempo/fade; this lets
              THIS artist override the site-wide default (Gordon 2026-09-20:
              "default tempo and fades that users can custom"). Leave blank
              to just use the site default from Super Admin. */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Card thumbnail autoplay (optional override)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Seconds/slide</Label>
                <Input
                  type="number" min={1} max={30} step={0.5} className="h-8 mt-0.5"
                  placeholder="Site default"
                  value={form.slideshowSettings.intervalSec ?? ''}
                  onChange={e => setForm(f => ({ ...f, slideshowSettings: { ...f.slideshowSettings, intervalSec: e.target.value ? Number(e.target.value) : undefined } }))}
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Fade seconds</Label>
                <Input
                  type="number" min={0.5} max={10} step={0.5} className="h-8 mt-0.5"
                  placeholder="Site default"
                  value={form.slideshowSettings.fadeSec ?? ''}
                  onChange={e => setForm(f => ({ ...f, slideshowSettings: { ...f.slideshowSettings, fadeSec: e.target.value ? Number(e.target.value) : undefined } }))}
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Transition</Label>
                <Select
                  value={form.slideshowSettings.transition || '__default'}
                  onValueChange={v => setForm(f => ({ ...f, slideshowSettings: { ...f.slideshowSettings, transition: v === '__default' ? undefined : v } }))}
                >
                  <SelectTrigger className="h-8 mt-0.5 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default">Site default</SelectItem>
                    {(Object.keys(TRANSITION_LABELS) as SlideshowTransition[]).map(k => (
                      <SelectItem key={k} value={k}>{TRANSITION_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Videos */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5"><Video className="h-3.5 w-3.5" />Videos</Label>

            {/* Primary video */}
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">Primary video</span>
              <div className="flex gap-2">
                <Input
                  value={form.videoUrl}
                  onChange={e => setForm(f => ({ ...f, videoUrl: e.target.value }))}
                  placeholder="YouTube URL or direct link"
                  className="text-sm flex-1"
                />
                {form.videoUrl && (
                  <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-destructive"
                    onClick={() => setForm(f => ({ ...f, videoUrl: '' }))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {form.videoUrl && extractYoutubeId(form.videoUrl) && (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-black max-w-xs">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(form.videoUrl)}?controls=0&mute=1`}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay"
                    title="Primary video preview"
                  />
                </div>
              )}
            </div>

            {/* Additional videos */}
            {form.videos.map((vurl, idx) => (
              <div key={idx} className="space-y-1.5">
                <span className="text-xs text-muted-foreground">Video {idx + 2}</span>
                <div className="flex gap-2">
                  <Input
                    value={vurl}
                    onChange={e => updateVideo(idx, e.target.value)}
                    placeholder="YouTube URL or direct link"
                    className="text-sm flex-1"
                  />
                  <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0 text-destructive"
                    onClick={() => removeVideo(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {vurl && extractYoutubeId(vurl) && (
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black max-w-xs">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractYoutubeId(vurl)}?controls=0&mute=1`}
                      className="absolute inset-0 w-full h-full"
                      allow="autoplay"
                      title={`Video ${idx + 2} preview`}
                    />
                  </div>
                )}
              </div>
            ))}

            <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={addVideo}>
              <PlusCircle className="h-3.5 w-3.5" />Add Video
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-border/40">
          <Button className="flex-1 bg-violet-600 hover:bg-violet-500 text-white" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {saving
              ? (form.images.some(u => u.startsWith('data:')) ? 'Uploading images…' : 'Saving…')
              : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
